<script>
export default {
  props: {
    conversationStarters: {
      type: Array,
      required: false,
      default: () => [],
    },
    newStarter: {
      type: Object,
      required: true,
    },
    isEditing: {
      type: Boolean,
      required: true,
    },
    canAddStarter: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    safeConversationStarters() {
      return this.conversationStarters || [];
    },
  },
  methods: {
    addConversationStarter() {
      this.$emit('add-starter');
    },
    editConversationStarter(index) {
      this.$emit('edit-starter', index);
    },
    deleteConversationStarter(index) {
      this.$emit('delete-starter', index);
    },
    resetNewStarter() {
      this.$emit('reset-starter');
    },
  },
};
</script>

<template>
  <div class="w-full mt-6">
    <h3 class="text-base font-medium mb-2">Conversation Starters</h3>
    <p class="text-sm text-slate-600 dark:text-slate-400 mb-4">
      Configure quick replies or links that will be shown to users
    </p>

    <!-- Add/Edit Conversation Starter Form -->
    <div
      class="border border-slate-100 dark:border-slate-800 rounded-md p-4 mb-4"
    >
      <div class="flex flex-row gap-4">
        <div class="w-1/3">
          <label class="block text-sm font-medium mb-1">View</label>
          <select
            v-model="newStarter.view"
            class="w-full border border-slate-200 dark:border-slate-700 rounded-md p-2"
          >
            <option value="home">Home</option>
            <option value="conversation">Conversation</option>
          </select>
        </div>
        <div class="w-2/3">
          <label class="block text-sm font-medium mb-1"
            >Text (Optional if URL provided)</label
          >
          <input
            v-model="newStarter.text"
            type="text"
            class="w-full border border-slate-200 dark:border-slate-700 rounded-md p-2"
            placeholder="Enter text for the conversation starter"
          />
        </div>
      </div>

      <div class="mb-4">
        <label class="block text-sm font-medium mb-1">URL (Optional)</label>
        <input
          v-model="newStarter.url"
          type="url"
          class="w-full border border-slate-200 dark:border-slate-700 rounded-md p-2"
          placeholder="https://example.com/page"
        />
      </div>

      <div v-if="newStarter.url" class="mb-4">
        <div class="flex items-center">
          <input
            id="preview-checkbox"
            v-model="newStarter.preview"
            type="checkbox"
            class="mr-2"
          />
          <label
for="preview-checkbox" class="text-sm font-medium"
            >Show preview</label
          >
        </div>
        <p class="text-xs text-slate-500 mt-1">
          When enabled, a link preview image will be shown next to the
          conversation starter text
        </p>
      </div>

      <div class="flex justify-end gap-2">
        <button
          type="button"
          class="px-4 py-2 text-sm text-slate-600 dark:text-slate-400"
          @click="resetNewStarter"
        >
          Cancel
        </button>
        <button
          type="button"
          class="px-4 py-2 text-sm bg-woot-500 text-white rounded-md disabled:opacity-50"
          :disabled="!canAddStarter"
          @click="addConversationStarter"
        >
          {{ isEditing ? 'Update' : 'Add' }} Starter
        </button>
      </div>
    </div>

    <!-- List of Conversation Starters -->
    <div class="max-h-80 overflow-y-auto">
      <table v-if="safeConversationStarters.length > 0" class="w-full">
        <thead class="bg-slate-50 dark:bg-slate-800">
          <tr>
            <th class="text-left p-2">View</th>
            <th class="text-left p-2">Text</th>
            <th class="text-left p-2">URL</th>
            <th class="text-left p-2">Preview</th>
            <th class="text-right p-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(starter, index) in safeConversationStarters"
            :key="index"
            class="border-b border-slate-100 dark:border-slate-800"
          >
            <td class="p-2">{{ starter.view }}</td>
            <td class="p-2">{{ starter.text || '—' }}</td>
            <td class="p-2">
              <span v-if="starter.url" class="truncate block max-w-xs">{{
                starter.url
              }}</span>
              <span v-else>—</span>
            </td>
            <td class="p-2">
              <span v-if="starter.url && starter.preview">Yes</span>
              <span v-else-if="starter.url">No</span>
              <span v-else>—</span>
            </td>
            <td class="p-2 text-right">
              <button
                type="button"
                class="text-woot-500 mr-2 text-sm"
                @click="editConversationStarter(index)"
              >
                Edit
              </button>
              <button
                type="button"
                class="text-red-500 text-sm"
                @click="deleteConversationStarter(index)"
              >
                Delete
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-else class="text-left py-6 text-slate-500 dark:text-slate-400">
        No conversation starters added yet
      </div>
    </div>
  </div>
</template>
